import type { RecipeDef } from "@reagent/sim";
import { GF_RECIPES } from "./stages/era1/gf";
import { GL_RECIPES } from "./stages/era1/gl";
import { MT_RECIPES } from "./stages/era1/mt";
import { PF_RECIPES } from "./stages/era1/pf";
import { TP_RECIPES } from "./stages/era1/tp";

/**
 * The reactions the stages are built from.
 *
 * Two engine facts shape every one of these:
 *
 *  - A node's whole buffer is pooled into one charge, and whatever a recipe
 *    doesn't consume rides along in the product packet. So a recipe with a
 *    byproduct yields a *mixture*, and mixtures are what poison catalyst beds
 *    and fail purity gates. That is the design lever, not an accident.
 *  - `nodeKinds` is per KIND, not per node: absent a binding, two mixers on
 *    the same board run every mixer recipe. A `NodeDef`/`deploysNodeConfig`
 *    may additionally carry `recipeIds` (spec 0031) to bind a node to a named
 *    subset of its kind-matching recipes, so two same-kind nodes can run
 *    different reactions, holds, boils or splits. Every Era 1 line module binds
 *    its pre-placed nodes this way (spec 0037).
 *
 * Era 1's recipes live with their pathway (`stages/era1/<id>.ts`); this file
 * keeps only what the two stages outside the pool use.
 */

/**
 * Kept for `stage-00-induction`, the tutorial, which stays outside the run
 * (spec 0037 "Inductions"). Textbook exothermic neutralisation, with water as byproduct.
 */
export const NEUTRALISATION: RecipeDef = {
  id: "neutralisation",
  inputs: { HCl: 1, NaOH: 1 },
  outputs: { NaCl: 1, H2O: 1 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

/**
 * Diagnostic fixture (spec 0017 T4): the board-size boundary stage places no
 * reactor, so nothing ever pools a buffer against this recipe. It exists only
 * because `stageSchema` requires at least one recipe; see
 * `stages/diagnostic-max-board.ts`.
 */
export const DIAGNOSTIC_PASSTHROUGH: RecipeDef = {
  id: "diagnostic-passthrough",
  inputs: { DiagnosticFeed: 1 },
  outputs: { DiagnosticFeed: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["reactor"],
};

export const ALL_RECIPES: readonly RecipeDef[] = [
  NEUTRALISATION,
  DIAGNOSTIC_PASSTHROUGH,
  ...TP_RECIPES,
  ...GF_RECIPES,
  ...PF_RECIPES,
  ...MT_RECIPES,
  ...GL_RECIPES,
];
